<template>
  <span class="rotating-word">
    <Transition name="word" mode="out-in">
      <span :key="current" class="inline-block text-gradient-primary whitespace-nowrap pr-[0.05rem]"
        >{{ current }}.</span
      >
    </Transition>

    <!-- Chalk underline: slight arc rising to the right, drawn in from the left.
         Keyed on the index (not inside the transition) so it starts redrawing just before the new word enters. -->
    <svg
      :key="index"
      class="chalk-underline"
      viewBox="0 0 200 30"
      preserveAspectRatio="none"
      aria-hidden="true">
      <defs>
        <linearGradient id="chalk-underline-gradient" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stop-color="rgb(255, 54, 150)" />
          <stop offset="100%" stop-color="rgb(255, 124, 83)" />
        </linearGradient>
        <filter id="chalk-underline-texture" x="-5%" y="-60%" width="110%" height="220%">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="3" seed="4" result="noise" />
          <feColorMatrix
            in="noise"
            type="matrix"
            values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  -1.8 0 0 0 1.75"
            result="grain" />
          <feComposite in="SourceGraphic" in2="grain" operator="in" result="speckled" />
          <feDisplacementMap
            in="speckled"
            in2="noise"
            scale="2.5"
            xChannelSelector="R"
            yChannelSelector="G" />
        </filter>
      </defs>
      <!-- Filled crescent instead of a stroke: thin at both ends, thickest in the middle -->
      <path
        d="M4 26 Q 88 2 196 14 Q 88 5 4 26 Z"
        fill="url(#chalk-underline-gradient)"
        stroke="url(#chalk-underline-gradient)"
        stroke-width="4.5"
        stroke-linejoin="round"
        filter="url(#chalk-underline-texture)" />
    </svg>
  </span>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const props = withDefaults(defineProps<{ words: string[]; interval?: number }>(), {
  interval: 2800,
})

const index = ref(0)
const current = computed(() => props.words[index.value])

let timer: ReturnType<typeof setInterval> | undefined

onMounted(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  timer = setInterval(() => {
    index.value = (index.value + 1) % props.words.length
  }, props.interval)
})

onBeforeUnmount(() => clearInterval(timer))
</script>

<style scoped>
.rotating-word {
  position: relative;
  display: inline-block;
}

.chalk-underline {
  position: absolute;
  left: 0;
  bottom: -0.25em;
  width: 100%;
  height: 0.45em;
  overflow: visible;
  pointer-events: none;
  opacity: 0.6;
  clip-path: inset(0 100% 0 0);
  animation: chalk-draw 0.7s cubic-bezier(0.65, 0, 0.35, 1) 0.15s forwards;
}

@keyframes chalk-draw {
  to {
    clip-path: inset(0 -5% 0 0);
  }
}

.word-enter-active,
.word-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.word-enter-from {
  opacity: 0;
  transform: translateY(0.25em);
}

.word-leave-to {
  opacity: 0;
  transform: translateY(-0.25em);
}

@media (prefers-reduced-motion: reduce) {
  .chalk-underline {
    animation: none;
    clip-path: none;
  }
}
</style>
